import { useCallback } from 'react';
import { usePaginatedData, UsePaginatedDataOptions, UsePaginatedDataResult } from './usePaginatedData';
import { getPaginatedPoets } from '@/services/poetService';
import type { Database } from '@/types/supabase';
import { PaginationParams } from '@/utils/pagination/paginationUtils';

// Define the Poet type based on the database schema
type Poet = Database['public']['Tables']['poets']['Row'];

// Cache TTL constants
const CACHE_TTL = {
  SHORT: 1 * 60 * 1000, // 1 minute
  MEDIUM: 5 * 60 * 1000, // 5 minutes
  LONG: 30 * 60 * 1000, // 30 minutes
};

// Cache key prefix for paginated poets
const PAGINATED_POETS_CACHE_KEY = 'poets:paginated:';

export interface UsePoetsOptions extends Omit<UsePaginatedDataOptions<Poet>, 'fetchFn' | 'cacheKey' | 'cacheTTL'> {
  // Whether to use caching (defaults to true)
  useCache?: boolean;
  
  // Custom cache TTL (defaults to MEDIUM)
  cacheTTL?: number;
}

/**
 * Hook for fetching a paginated list of poets
 * 
 * @param options - Configuration options for the hook
 * @returns Object containing the paginated poets data, pagination controls, loading state, and error
 * 
 * Note: This hook returns all poets regardless of whether they have poems or if their poems
 * are archived. The filtering of poems based on archive status happens in the usePoems hook
 * and is applied when displaying poet data in the UI.
 */
export function usePoets(
  options: UsePoetsOptions
): UsePaginatedDataResult<Poet> {
  const { 
    useCache = true, 
    cacheTTL = CACHE_TTL.MEDIUM,
    ...restOptions 
  } = options;

  // Create a fetch function for the poets
  const fetchPoets = useCallback(
    (params: PaginationParams) => getPaginatedPoets(params),
    []
  );

  // Create a cache key for the poets
  const cacheKey = useCache ? PAGINATED_POETS_CACHE_KEY : undefined;

  // Use the paginated data hook
  return usePaginatedData<Poet>({
    ...restOptions,
    fetchFn: fetchPoets,
    cacheKey,
    cacheTTL
  });
} 